import {
  type ConsentPreferences,
  type GoogleConsentState,
  type GoogleConsentValue,
  type MicrosoftConsentState,
} from "./consentTypes";
import { buildConsentPreferences, readStoredConsentPreferences, saveConsentPreferences } from "./consentStorage";

type GtagConsentFn = (
  command: "consent" | "set",
  action: "default" | "update" | string,
  params: unknown,
) => void;

type UetQueue = Array<unknown> | { push: (...args: unknown[]) => unknown };

declare global {
  interface Window {
    uetq?: UetQueue;
    __JUSTICEDRIVE_INITIAL_CONSENT__?: ConsentPreferences | null;
    loadJusticeDriveMarketingTags?: (preferences: Pick<ConsentPreferences, "analytics" | "advertising">) => void;
  }
}

const consentValue = (allowed: boolean): GoogleConsentValue => (allowed ? "granted" : "denied");

export const mapGoogleConsent = (preferences: Pick<ConsentPreferences, "analytics" | "advertising">): GoogleConsentState => ({
  analytics_storage: consentValue(preferences.analytics),
  ad_storage: consentValue(preferences.advertising),
  ad_user_data: consentValue(preferences.advertising),
  ad_personalization: consentValue(preferences.advertising),
});

export const mapMicrosoftConsent = (
  preferences: Pick<ConsentPreferences, "advertising">,
): MicrosoftConsentState => ({
  ad_storage: consentValue(preferences.advertising),
});

export const getInitialConsentPreferences = (): ConsentPreferences | null => {
  if (typeof window === "undefined") {
    return null;
  }
  return window.__JUSTICEDRIVE_INITIAL_CONSENT__ ?? readStoredConsentPreferences();
};

export const applyConsentPreferences = (
  preferences: ConsentPreferences,
  mode: "default" | "update" = "update",
): void => {
  if (typeof window === "undefined") {
    return;
  }
  const googleConsent = mapGoogleConsent(preferences);
  try {
    const gtag = window.gtag as GtagConsentFn | undefined;
    gtag?.("consent", mode, googleConsent);
    gtag?.("set", "ads_data_redaction", !preferences.advertising);
    gtag?.("set", "url_passthrough", true);
    window.loadJusticeDriveMarketingTags?.(preferences);
  } catch {
    // Consent updates must not block product flows.
  }

  try {
    window.uetq = window.uetq ?? [];
    window.uetq.push("consent", mode, mapMicrosoftConsent(preferences));
  } catch {
    // Consent updates must not block product flows.
  }
};

export const persistAndApplyConsentPreferences = (
  values: Pick<ConsentPreferences, "analytics" | "advertising">,
): ConsentPreferences => {
  const preferences = buildConsentPreferences(values);
  saveConsentPreferences(preferences);
  applyConsentPreferences(preferences, "update");
  window.dispatchEvent(new CustomEvent("justicedrive:cookie-consent-changed", { detail: preferences }));
  return preferences;
};

export const acceptAllConsent = (): ConsentPreferences =>
  persistAndApplyConsentPreferences({ analytics: true, advertising: true });

export const rejectOptionalConsent = (): ConsentPreferences =>
  persistAndApplyConsentPreferences({ analytics: false, advertising: false });
